import Container, { Service } from "typedi";
import IHttpClientGetParameters from "@/modules/common/contracts/http-client/http-client-get-parameters.interface";
import { AxiosResponse } from "axios";
import HttpClient from "@/modules/common/services/http-client.service";
import NotificationService from "@/modules/common/services/notification.service";
import IHttpClientPostParameters from '@/modules/common/contracts/http-client/http-client-post-parameters.interface'
import {QuizResult, UserSelections, IEventDetails} from '@/modules/learning/learning.model'


@Service()
export default class LearningService {
       // Fetch event/course details
    public async getEvent(event_uuid: string): Promise<IEventDetails> {
        try {
            const httpParams: IHttpClientGetParameters = {
                url: `/event/${event_uuid}/`,
                requireToken: true
            };
            const { data }: AxiosResponse = await Container.get(HttpClient).get(httpParams, event_uuid);

            if (!data || !data.module_type || !data.uuid) {
                throw new Error("Invalid event data received from API. Missing module_type or uuid.");
            }

            return data as IEventDetails;
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            Container.get(NotificationService).error(message);
            throw error;
        }
    }

    // Generate launch URL for SCORM course
    public async launchScormCourse(eventUuid: string, scormCourseId: string, returnUrl: string): Promise<string> {
        try {
            const httpParams = {
                url: `/event/${eventUuid}/scorm-course/launch`,
                requireToken: true,
                payload: {
                    redirect_on_exit_url: returnUrl
                }
            };

            const response: AxiosResponse = await Container.get(HttpClient).post(httpParams, eventUuid);

            if (response.data && response.data.launch_url) {
                return response.data.launch_url as string;
            } else {
                throw new Error("SCORM Launch API did not return a valid launch URL.");
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            Container.get(NotificationService).error(message);
            throw error;
        }
    }

    public async getContent (course_uuid: string, chapter_id: string, content_id: string): Promise<any> {
        const httpParams: IHttpClientGetParameters = {
            url: `/event/${course_uuid}/contents/${chapter_id}/${content_id}`,
            requireToken: true
        }
        const { data }: AxiosResponse = await Container.get(HttpClient).get(httpParams, course_uuid);
        return data
    }

    public async getContentByType (course_uuid: string, type: string, options?): Promise<AxiosResponse> {
        const { no_data } = options || {}
        const httpParams: IHttpClientGetParameters = {
            url: `/event/${course_uuid}/public/content/${type}` + (no_data ? '&no_data=true' : ''),
            requireToken: true
        }
        return await Container.get(HttpClient).get(httpParams)
    }

    public async getChapter (course_uuid: string, chapter_uuid: string): Promise<any> {
        const httpParams: IHttpClientGetParameters = {
            url: `/event/${course_uuid}/chapter/${chapter_uuid}/contents`,
            requireToken: true
        }
        const { data }: AxiosResponse = await Container.get(HttpClient).get(httpParams, course_uuid);
        return data
    }

    public async getCourse (course_uuid: string): Promise<any> {
        const httpParams: IHttpClientGetParameters = {
            url: `/event/${course_uuid}`,
            requireToken: true
        }
        return await Container.get(HttpClient).get<any>(httpParams)
    }

    public async getChapters (course_uuid: string): Promise<any> {
        try {
            const httpParams: IHttpClientGetParameters = {
                url: `/event/${course_uuid}/chapters`,
                requireToken: true
            }
            const { data }: AxiosResponse = await Container.get(HttpClient).get(httpParams, course_uuid)
            return data
        } catch (error) {
            Container.get(NotificationService).error(error)
        }
    }

    public async getSurveys(event_uuid: string, event_identifier: string) {
        const httpParams: IHttpClientGetParameters = {
            url: `/event/${event_uuid}/participant/me/surveys`,
            requireToken: true
        }
        const response: any = await Container.get(HttpClient).get(httpParams, event_identifier)
        return response
    }

    public async getTableOfContents(course_uuid: string) {
        const httpParams: IHttpClientGetParameters = {
            url: `/event/${course_uuid}/course-contents`,
            requireToken: true
        }
        const response: any = await Container.get(HttpClient).get(httpParams, course_uuid)
        return response
    }

    public async saveProgress(course_uuid: string, chapter_uuid: string, assessment: string, result_uuid: string,  payload: UserSelections): Promise<AxiosResponse<{ passed: boolean; score: number }>> {
        const httpParams: IHttpClientPostParameters = {
            url: `/event/${course_uuid}/participant/me/chapter/${chapter_uuid}/assessment/${assessment}/save/${result_uuid}`,
            requireToken: true,
            payload
        }
        return await Container.get(HttpClient).post(httpParams)
    }

    public async submitSurveyAnswer(course_uuid: string, chapter_uuid: string, assessment: string, result_uuid: string,  payload: UserSelections): Promise<AxiosResponse<{ passed: boolean; score: number }>> {
        const httpParams: IHttpClientPostParameters = {
            url: `/event/${course_uuid}/participant/me/chapter/${chapter_uuid}/assessment/${assessment}/grade/${result_uuid}`,
            requireToken: true,
            payload
        }
        return await Container.get(HttpClient).post(httpParams)
    }

    public async startQuiz(course_uuid: string, chapter_uuid: string, assessment: string): Promise<AxiosResponse<{ attempt_details: QuizResult; time_left: number | null }>> {
        const httpParams: IHttpClientPostParameters = {
            url: `/event/${course_uuid}/participant/me/chapter/${chapter_uuid}/assessment/${assessment}/start`,
            requireToken: true,
            payload: {}
        }
        return await Container.get(HttpClient).post(httpParams)
    }

    public async getAttempts(course_uuid: string, content_id: string): Promise<AxiosResponse<number>> {
        const httpParams: IHttpClientGetParameters = {
            url: `/event/${course_uuid}/participant/me/${content_id}/attempts`,
            requireToken: true,
        }
        return await Container.get(HttpClient).get(httpParams)
    }
}
